/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useEffect, useState } from 'react';
import {
  closestCenter, DndContext, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  arrayMove, SortableContext, useSortable, verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Route, Routes, useParams } from 'react-router-dom';
import {
  Badge, Button, Card, Col, Modal, Row, Tab, Tabs,
} from 'react-bootstrap';
import moment from 'moment';
import UserPill from '../../common/components/UserPill';
import { BasicPeopleLayout } from '../layout/PeopleLayout';
import { Icon, LoadingAnimation } from '../../common/components/Utilities';
import Notes from '../../common/components/Notes';
import DataTable from '../../common/components/DataTable';
import CandidateResource from '../../common/resource/CandidateResource';
import LegacyUserResource from '../../common/resource/LegacyUserResource';
import BasicPage from '../../common/layout/BasicPage';
import { CreateButton, EditButton } from '../../common/components/Buttons';
import { can } from '../../common/Util';
import RestrictedContent from '../../common/components/RestrictedContent';

function RecruitmentCandidates(props) {
  return (
    <BasicPeopleLayout {...props}>
      <Routes>
        <Route path="/:vacancyId/candidates/:uuid" element={<CandidateShowPage {...props} />} />

        <Route path="/:vacancyId/candidates" element={<CandidateList {... props} />} />

        <Route path="/" element={<VacancyList {... props} />} />
      </Routes>
    </BasicPeopleLayout>
  );
}

function VacancyList({ groups }) {
  const [loading, setLoading] = useState(true);
  const [vacancies, setVacancies] = useState([]);
  const [updated, setUpdated] = useState([]);

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setVacancies((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        const newOrder = [];
        const newVacancies = arrayMove(items, oldIndex, newIndex).map((vacancy, idx) => {
          // eslint-disable-next-line no-param-reassign
          vacancy.new_order = idx + 1;

          if (vacancy.order !== vacancy.new_order) {
            newOrder.push({ id: vacancy.id, order: vacancy.new_order });
          }

          return vacancy;
        });

        setUpdated(newOrder);
        return newVacancies;
      });
    }
  };

  const load = async () => {
    setLoading(true);
    const newVacancies = await CandidateResource.vacancies();

    setVacancies(newVacancies.map((vacancy) => {
      // eslint-disable-next-line no-param-reassign
      vacancy.new_order = vacancy.order;
      return vacancy;
    }));

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  if (!can('setRecruitmentPriorities')) {
    return (
      <BasicPage title="Active Vacancies">
        <Row>
          <Col>
            {
              vacancies.map((vacancy) => (
                <VacancyItem
                  key={vacancy.id}
                  vacancy={vacancy}
                />
              ))
            }
          </Col>
          <Col md={3}>
            <TeamSize groups={groups} />
          </Col>
        </Row>
      </BasicPage>
    );
  }

  return (
    <BasicPage
      title="Active Vacancies"
      action={(
        <form action="/recruitment/priorities" method="POST">
          <input type="hidden" name="_token" value={window.props.csrfToken} />

          {updated.map((item) => (
            <input type="hidden" key={item.id} name={`updated[${item.id}]`} value={item.order} />
          ))}
          <CreateButton
            type="submit"
            icon="check"
            disabled={(loading || updated.length === 0)}
          >
            Save Priorities
          </CreateButton>
        </form>
      )}
    >
      <Row>
        <Col>
          <LoadingAnimation loading={loading} />
          {!loading && vacancies.length && (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleDragEnd}
            >
              <SortableContext
                items={vacancies}
                strategy={verticalListSortingStrategy}
              >
                {!loading && vacancies.length && vacancies.map((vacancy) => (
                  <VacancyItem
                    key={vacancy.id}
                    vacancy={vacancy}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </Col>

        <Col md={3}>
          <TeamSize groups={groups} />
        </Col>
      </Row>
    </BasicPage>
  );
}

export function CandidateShowPage() {
  const { uuid } = useParams();
  const [loading, setLoading] = useState(true);
  const [candidate, setCandidate] = useState({});

  const load = async () => {
    setLoading(true);
    setCandidate(await CandidateResource.get(uuid));
    setLoading(false);
  };

  useEffect(() => { load(); }, [uuid]);

  return (
    <BasicPage
      title={candidate.name}
      action={<EditButton href={`/candidates/${candidate.uuid}/edit`}>Edit Candidate</EditButton>}
    >
      <LoadingAnimation loading={loading} />
      {!loading && <CandidateDetails candidate={candidate} />}
    </BasicPage>
  );
}

function CandidateList() {
  const { vacancyId } = useParams();
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [activeFilters, setFilters] = useState({
    statuses: [],
    vacancies: [vacancyId],
  });

  const [loading, setLoading] = useState(true);
  const [statuses, setStatuses] = useState([]);
  const [vacancy, setVacancy] = useState({});

  const load = async () => {
    setLoading(true);
    const newStatuses = await CandidateResource.statuses();

    const newFilters = {
      vacancies: [vacancyId],
      statuses: newStatuses.filter((item) => item.in_process).map((item) => item.id),
    };

    setStatuses(newStatuses);
    setFilters(newFilters);

    setVacancy(await CandidateResource.vacancy(vacancyId));

    setLoading(false);
  };

  useEffect(
    () => {
      load();
    },
    [vacancyId],
  );

  const changeStatuses = (event) => {
    const value = parseInt(event.target.value, 10);
    const newFilters = { ...activeFilters };

    if (event.target.checked && !newFilters.statuses.includes(value)) {
      newFilters.statuses.push(value);
    }

    if (!event.target.checked && newFilters.statuses.includes(value)) {
      newFilters.statuses = newFilters.statuses.filter((item) => item !== value);
    }

    setFilters(newFilters);
  };

  return (
    <BasicPage
      action={(<CreateButton href={`/candidates/create?vacancy_id=${vacancyId}`} icon="user-plus">Add Candidate</CreateButton>)}
      title={!loading && (<VacancyTitle vacancy={vacancy} />)}
    >
      <LoadingAnimation loading={loading} />
      <Row>
        <Col md={9}>
          {!loading && (
            <DataTable
              url="/resource/candidates"
              filters={activeFilters}
              columns={['', 'Name', 'Status', 'Location']}
              renderRow={(candidate) => (
                <CandidateRow
                  key={candidate.id}
                  candidate={candidate}
                  setSelectedCandidate={setSelectedCandidate}
                />
              )}
              noResultsMessage="Sorry, we couldn't find any candidates."
            />
          )}
        </Col>

        <Col md={3}>
          <Card className="mb-2">
            <Card.Header>Status</Card.Header>
            <Card.Body>
              {statuses.map((status) => (
                <div key={`status-${status.id}`} className="form-group form-check" style={{ marginBottom: '4px' }}>
                  <input id={`status-${status.id}`} checked={activeFilters.statuses.includes(status.id)} value={status.id} type="checkbox" className="form-check-input" onChange={changeStatuses} />
                  <label htmlFor={`status-${status.id}`} className={`form-check-label ${status.in_process ? 'text-success' : 'text-muted'}`}>{status.title}</label>
                </div>
              ))}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <CandidateShow candidate={selectedCandidate} setSelectedCandidate={setSelectedCandidate} />
    </BasicPage>
  );
}

function CandidateRow({ candidate, setSelectedCandidate }) {
  return (
    <tr>
      <td style={{ verticalAlign: 'middle' }}>
        <img
          alt="Candidate profile"
          className="rounded-circle"
          style={{ width: '32px', height: '32px' }}
          src={`${candidate.gravatar}&s=32`}
        />
      </td>
      <td style={{ verticalAlign: 'middle', fontSize: '1.3em' }}>
        <Button variant="link" className="p-0" onClick={() => { setSelectedCandidate(candidate); }}>{candidate.name}</Button>
        <div className="text-muted small">{candidate.job.title}</div>
      </td>

      <td style={{ verticalAlign: 'middle' }}>
        {candidate.status.title}
        <div className="text-info">
          Last updated
          {' '}
          {moment(candidate.updated_at).format('D MMMM YYYY')}
        </div>
      </td>

      <td style={{ verticalAlign: 'middle' }}>
        {candidate.location.name}
      </td>
    </tr>
  );
}

function CandidateShow({ candidate, setSelectedCandidate }) {
  if (!candidate) {
    return '';
  }

  return (
    <Modal size="lg" show onHide={() => setSelectedCandidate(null)}>
      <Modal.Header closeButton>
        <Modal.Title>
          {candidate.name}
        </Modal.Title>
        <a href={`/candidates/${candidate.uuid}/edit`} className="btn btn-sm btn-outline-warning ml-4"><Icon icon="edit" /></a>
      </Modal.Header>

      <Modal.Body>
        <CandidateDetails candidate={candidate} />
      </Modal.Body>
    </Modal>
  );
}

function CandidateDetails({ candidate, activeUser }) {
  const [showSalary, setShowSalary] = useState(false);

  const statuses = candidate.statuses || [];

  return (
    <Row>
      <Col md={4} style={{ textAlign: 'center' }}>
        <div>
          <img alt="Candidate profile" src={`${candidate.gravatar}&s=300`} style={{ width: '300px', maxWidth: '100%' }} className="rounded-circle mb-4" />
        </div>
        <a style={{ display: candidate.linkedin ? '' : 'none' }} target="_blank" href={candidate.linkedin} className="mx-1 btn btn-lg btn-outline-success" rel="noreferrer"><Icon icon="linkedin" append="fa-fw" /></a>
        <a style={{ display: candidate.cv ? '' : 'none' }} target="_blank" href={`/candidates/${candidate.uuid}/cv`} className="mx-1 btn btn-lg btn-outline-success" rel="noreferrer"><Icon icon="file-download" append="fa-fw" /></a>
      </Col>

      <Col md={8}>
        <Tabs defaultActiveKey="details" id="candidate-tabs" mountOnEnter>
          <Tab eventKey="details" title="Details">
            <table className="table">
              <tbody>
                <tr>
                  <th colSpan={2} style={{ background: '#f0f0f0' }}>Recruitment</th>
                </tr>

                <tr style={{ display: candidate.vacancy_id ? '' : 'none' }}>
                  <th>Vacancy</th>
                  <td>
                    <VacancyLink vacancy={candidate.vacancy} />
                  </td>
                </tr>

                <tr style={{ display: candidate.recruiter_id ? '' : 'none' }}>
                  <th>Recruiter</th>
                  <td><UserPill user={candidate.recruiter} /></td>
                </tr>

                <tr style={{ display: candidate.candidate_source_id ? '' : 'none' }}>
                  <th>Source</th>
                  <td>{candidate.candidate_source_id ? candidate.source.title : ''}</td>
                </tr>

                <tr style={{ display: candidate.status_id ? '' : 'none' }}>
                  <th>Status</th>
                  <td>{candidate.status_id ? candidate.status.title : ''}</td>
                </tr>

                <tr style={{ display: candidate.prescreen_completed ? '' : 'none' }}>
                  <th>Prescreen Completed?</th>
                  <td className="text-success">
                    {candidate.prescreen_completed ? (
                      <div>
                        <Icon icon="check-circle" />
                        {' '}
                        Yes
                      </div>
                    ) : 'No'}
                  </td>
                </tr>

                <tr style={{ display: candidate.agency_exclusivity_date ? '' : 'none' }}>
                  <th>Agency Exclusivity</th>
                  <td>{candidate.agency_exclusivity_date ? moment(candidate.agency_exclusivity_date).format('D MMMM YYYY') : ''}</td>
                </tr>

                <tr>
                  <th colSpan={2} style={{ background: '#f0f0f0' }}>Candidate</th>
                </tr>
                <tr style={{ display: candidate.email ? '' : 'none' }}>
                  <th>Email</th>
                  <td><a href={`mailto:${candidate.email}`}>{candidate.email}</a></td>
                </tr>

                <tr style={{ display: candidate.phone ? '' : 'none' }}>
                  <th>Phone</th>
                  <td><a href={`tel:${candidate.phone}`}>{candidate.phone}</a></td>
                </tr>

                <tr style={{ display: candidate.address ? '' : 'none' }}>
                  <th>Address</th>
                  <td>{candidate.address}</td>
                </tr>

                <tr style={{ display: window.props.activeUser.isInGroup(window.props.groups.CAN_VIEW_CANDIDATE_SALARIES) ? '' : 'none' }}>
                  <th colSpan={2} style={{ background: '#f0f0f0' }}>Salary</th>
                </tr>

                <tr style={{ display: candidate.display_salary ? '' : 'none' }}>
                  <th>Salary Expectation</th>
                  <td style={{ cursor: 'pointer' }} onClick={() => setShowSalary(!showSalary)}>{showSalary ? candidate.display_salary : <i>Click to display</i>}</td>
                </tr>

                <tr style={{ display: candidate.display_previous_salary ? '' : 'none' }}>
                  <th>Current/Previous Salary</th>
                  <td style={{ cursor: 'pointer' }} onClick={() => setShowSalary(!showSalary)}>{showSalary ? candidate.display_previous_salary : <i>Click to display</i>}</td>
                </tr>

                <tr style={{ display: candidate.display_salary_offered ? '' : 'none' }}>
                  <th>Salary Offered</th>
                  <td style={{ cursor: 'pointer' }} onClick={() => setShowSalary(!showSalary)}>{showSalary ? candidate.display_salary_offered : <i>Click to display</i>}</td>
                </tr>

                <tr>
                  <th colSpan={2} style={{ background: '#f0f0f0' }}>Admin</th>
                </tr>

                <tr style={{ display: candidate.job_id ? '' : 'none' }}>
                  <th>Role</th>
                  <td>{candidate.job_id ? candidate.job.title : ''}</td>
                </tr>

                <tr style={{ display: candidate.level ? '' : 'none' }}>
                  <th>Level</th>
                  <td>{candidate.level ? candidate.level : ''}</td>
                </tr>

                <tr style={{ display: candidate.notice_period_id ? '' : 'none' }}>
                  <th>Notice Period</th>
                  <td>{candidate.notice_period_id ? candidate.notice.title : ''}</td>
                </tr>

                <tr style={{ display: candidate.location_id ? '' : 'none' }}>
                  <th>Location</th>
                  <td>{candidate.location_id ? candidate.location.name : ''}</td>
                </tr>
              </tbody>
            </table>
          </Tab>

          <Tab eventKey="timeline" title="Timeline">
            <table className="table">
              <tbody>
                {statuses.map((status) => (
                  <tr key={status.pivot.id}>
                    <th>{moment(status.pivot.created_at).format('D MMMM YYYY HH:mm')}</th>
                    <td className={`text-${status.in_process ? 'success' : 'danger'}`}>{status.title}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Tab>

          <Tab eventKey="notes" title="Notes">
            <Notes modelType="App\Candidate" modelId={candidate.id} sessionUser={activeUser} />
          </Tab>
        </Tabs>
      </Col>
    </Row>
  );
}

function VacancyTitle({ vacancy }) {
  return (
    <>
      <strong>{vacancy.role && vacancy.role.title}</strong>
      {' '}
      <span className="small text-muted">
        for
        {' '}
        {vacancy.role && vacancy.squad.name}
      </span>
    </>
  );
}

function VacancyLink({ vacancy }) {
  if (!vacancy) {
    return '';
  }

  return (
    <a href={`/recruitment/${vacancy.id}/candidates`}>
      <VacancyTitle vacancy={vacancy} />
    </a>
  );
}

function VacancyItem({ vacancy }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({ id: vacancy.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div className="card mb-2" ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <div className="card-body" style={{ borderLeft: vacancy.order !== vacancy.new_order ? '5px solid rgb(240 128 0)' : '' }}>
        <OrderNumbers vacancy={vacancy} />
        <a href={`/recruitment/${vacancy.id}/candidates`} style={{ color: '#525f7f', fontWeight: vacancy.order !== vacancy.new_order ? 700 : 300 }}>
          <VacancyTitle vacancy={vacancy} />
        </a>

        <ApproverBadge user={vacancy.approver} />
      </div>
    </div>
  );
}

function OrderNumbers({ vacancy }) {
  let prev;

  if (vacancy.order !== vacancy.new_order) {
    prev = (
      <>
        <span style={{ fontSize: '0.9em' }} className="text-danger mr-2">
          #
          {vacancy.order}
        </span>
        <Icon icon="arrow-right" variant="fad" append="mr-2" />
      </>
    );
  }

  return (
    <>
      {prev}

      <span style={{ fontSize: '1.2em' }} className="text-success mr-2">
        #
        {vacancy.new_order}
      </span>
    </>
  );
}

function TeamSize({ groups }) {
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [notStarted, setNotStarted] = useState([]);
  const [leaving, setLeaving] = useState([]);

  const load = async () => {
    setLoading(true);

    setUsers(await LegacyUserResource.index({
      started: 1,
      leaver: 0,
      only_count: 1,
      only_tech: 1,
    }));

    setNotStarted(await LegacyUserResource.index({
      not_started: 1,
      only_count: 1,
      only_tech: 1,
    }));

    setLeaving(await LegacyUserResource.index({
      leaver: 1,
      only_count: 1,
      only_tech: 1,
    }));

    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="card-stat">
      <Card.Body style={{ position: 'relative' }}>
        <LoadingAnimation loading={loading} />

        <Row>
          <Col>
            <h4 className="text-muted">Team Size</h4>
            <div className="stat my-2">
              {users + notStarted}
              {' '}
              {users === 1 ? 'person' : 'people'}
            </div>
            <div className="small text-muted">
              {`${users + leaving} ${users + leaving === 1 ? 'person' : 'people'} in the team.`}
            </div>

            <RestrictedContent group={groups.TEAM_LEADS}>

              <div className="small text-danger">
                {`- ${leaving} ${leaving === 1 ? 'person' : 'people'} due to leave.`}
              </div>
              <div className="small text-success">
                {`+ ${notStarted} ${notStarted === 1 ? 'person' : 'people'} due to start.`}
              </div>

            </RestrictedContent>

          </Col>

          <Col sm="auto">
            <div className="icon">
              <Icon variant="fad" icon="users" size="3x" fixed />
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
}

function ApproverBadge({ user }) {
  return (
    <Badge variant="success" style={{ float: 'right' }}>
      <Icon icon="check" variant="fad" append="mr-2" />
      {`Approved by ${user.name}`}
    </Badge>
  );
}

export default RecruitmentCandidates;
